import { useEffect, useState } from "react";

import { Box, IconButton, Stack, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

import useLocalization from "../../hooks/useLocalization";
import useSurvey from "../../hooks/useSurvey";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

import Input from "../elements/Input";
import EditorToolbar from "./EditorToolbar";
import GetLocalFile from "../elements/GetLocalFile";
import Modal from "../modals/Modal";

const MatrixItemEditor = ({ surveyId, matrixItems, onSave }) => {
  const { getMessage, getLabel, getTitle } = useLocalization();
  const { importSKUMatrix } = useSurvey();
  const { excelToJson } = useData();
  const { showMessage } = usePage();

  const [openImport, setOpenImport] = useState(false);
  const [newItems, setNewItems] = useState(
    Array.isArray(matrixItems) ? matrixItems : []
  );

  const handleChange = (index, field, newValue) => {
    setNewItems((current) => {
      let items = [...current];
      items[index][field] = newValue;

      return items;
    });
  };

  const handleSave = () => {
    onSave(newItems);
  };

  const handleAdd = () => {
    setNewItems((current) => {
      return [...current, { name: "", name_fr: "" }];
    });
  };

  const handleDelete = (index) => {
    setNewItems((current) => {
      let items = [...current];
      items.splice(index, 1);

      return items;
    });
  };

  const handleCustomOption = (option) => {
    if (option === "importSKU") {
      setOpenImport(true);
    }
  };

  const handleImportFile = (file) => {
    excelToJson(file, fileConverted);
  };

  const fileConverted = (json) => {
    if (!json || json.length === 0) return;

    let keys = Object.keys(json[0]);
    if (!keys.includes("Name")) {
      showMessage(getMessage("missingName", "error"));
      return;
    }

    setNewItems((current) => {
      let items = [];

      for (const item of json) {
        let newItem = {
          name: item.Name,
          name_fr: keys.includes("French") ? item.French : null,
          code: keys.includes("UPC") ? item.UPC : null,
        };
        if (keys.includes("ROI_Distribution") || keys.includes("ROI_OOS")) {
          newItem.roi = {
            distribution: keys.includes("ROI_Distribution")
              ? item.ROI_Distribution
              : null,
            outofstock: keys.includes("ROI_OOS") ? item.ROI_OOS : null,
          };
        }

        newItem.udf = [];
        for (const key of keys) {
          if (
            ["UPC", "Name", "French", "ROI_Distribution", "ROI_OOS"].includes(
              key
            )
          )
            continue;

          newItem.udf.push({ label: key, value: item[key] });
        }

        items.push(newItem);
      }
      return items;
    });
  };

  return (
    <>
      <Box height="calc(100% - 20px)">
        <EditorToolbar
          onAdd={handleAdd}
          onSave={handleSave}
          customOptions={[
            { name: "importSKU", iconName: "Upload", label: "SKU" },
          ]}
          onCustomOption={handleCustomOption}
        />
        <Box
          padding="10px"
          height="calc(100% - 30px)"
          overflow="auto"
        >
          {Array.isArray(newItems) && newItems.length > 0 ? (
            <Stack spacing={1}>
              {newItems.map((item, index) => (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                >
                  <Box sx={{ width: "400px" }}>
                    <Input
                      label={getLabel("name")}
                      value={item.name}
                      onChange={(newValue) =>
                        handleChange(index, "name", newValue)
                      }
                      fullwidth
                    />
                  </Box>
                  <Box sx={{ width: "400px" }}>
                    <Input
                      label={getLabel("name_fr")}
                      value={item.name_fr}
                      onChange={(newValue) =>
                        handleChange(index, "name_fr", newValue)
                      }
                      fullwidth
                    />
                  </Box>

                  <Box sx={{ width: "300px" }}>
                    <Input
                      label={getLabel("code")}
                      value={item.code}
                      onChange={(newValue) =>
                        handleChange(index, "code", newValue)
                      }
                      fullwidth
                    />
                  </Box>

                  <IconButton onClick={() => handleDelete(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography>{getMessage("noMatrixItems")}</Typography>
          )}
        </Box>
      </Box>

      <Modal
        open={openImport}
        onClose={() => setOpenImport(false)}
        title={getTitle("import_skuMatrix")}
      >
        <Box sx={{ padding: "24px" }}>
          <GetLocalFile onUploaded={handleImportFile} />
        </Box>
      </Modal>
    </>
  );
};

export default MatrixItemEditor;
